import { useLogin } from "@/components/LoginModal";
import { TrackerA, TrackerDiv } from "@/components/Tracker";
import { PermissionCodes } from "@/constants/permissionCodes";
import { ORGS_CLASS_MAP } from "@/pageComponents/GdMerge/shared";
import { isBigCompanyByOrg } from "@/utils/gaodingVip";
import { useGdVipInfo } from "@/utils/hooks/useGdVipInfo";
import { usePermission } from "@/utils/hooks/usePermisson";

import { BuyVipDialog } from "@/components/Vip";
import useAppStore from "@/context/store";
import { getDeliveryDetail } from "@/services/qingzhou";
import { CustomerServiceOutlined, RightOutlined } from "@ant-design/icons";
import { Dropdown, Modal } from "antd";
import React, { useEffect, useState } from "react";
import styles from "./MembershipDropdown.module.less";

import { getGdDeliveryDetail } from "@/services/gaoding";
import { getExpsValue } from "@/tracker/abTest";

interface MembershipDropdownProps {
  className?: string;
}

const codeMap: Record<string, string> = {
  gdmaterial: "download",
  gdesign: "template",
};

/**
 * 转换 skuItem.button_name 的值
 * @param {any} skuItemConfig - skuItem 配置对象
 * @param {boolean} isOrg - 是否为团队版
 * @param {any} priceResource - getVipPricingConfig 获取到的价格对象
 * @returns {string} - 转换后的 button_name 字符串
 */
function getSkuItemButtonText(
  skuItemConfig,
  priceResource: any,
  { isOrg }: { isOrg: boolean }
) {
  const base = isOrg ? "team" : "basic";
  const code =
    base +
    "-" +
    (skuItemConfig.trade_type === "plus"
      ? "plus"
      : codeMap[skuItemConfig.seat_type]);
  const price = priceResource?.version?.[code]?.price || "";
  const unit = priceResource?.version?.[code]?.price_unit || "";
  return skuItemConfig.button_name
    .replace("{price}", price)
    .replace("{unit}", unit);
}

const MembershipDropdown: React.FC<MembershipDropdownProps> = ({
  className,
}) => {
  const user = useAppStore((state) => state.user);
  const orgInfo = useAppStore((state) => state.orgInfo);
  const { data: gdVipInfo } = useGdVipInfo();

  const userPermissions = useAppStore((state) => state.userPermissions);
  const [config, setConfig] = useState<any>(null);
  const [pricingConfig, setPricingConfig] = useState<any>(null);

  const { login } = useLogin();
  const hasOfficialWebNavigationPermission = usePermission(
    PermissionCodes.officialWebNavigation
  );

  const hasPermission = (permission: string) => {
    if (!permission) return true;
    return userPermissions[permission];
  };

  useEffect(() => {
    const fetchConfig = async () => {
      const layoutABTest = await getExpsValue("hb_layout_update", "string_0");

      let dropdownResult;
      // 面板取花瓣配置
      if (layoutABTest === "new2") {
        dropdownResult = await getDeliveryDetail("framework_v2--vip_entrance");
      } else {
        dropdownResult = await getDeliveryDetail("framework--vip_entrance");
      }

      const dropdownConfig = dropdownResult?.pit_modules?.[0]?.material;
      // 价格取稿定订购页配置
      const pricingResult = await getGdDeliveryDetail("vip-plus-buy-page");
      const pricingConfig = pricingResult?.pit_modules?.[0]?.material;

      if (dropdownConfig) setConfig(dropdownConfig);
      if (pricingConfig) setPricingConfig(pricingConfig);
    };
    fetchConfig();
  }, [user]);

  // 配置为空时不展示
  if (!config) return null;

  // 企业版不展示
  if (isBigCompanyByOrg(orgInfo?.org)) return null;

  // 没登录或权限点判断没有权限时不展示
  if (!user || !hasOfficialWebNavigationPermission) return null;

  const handleVIPButtonClick = async () => {};

  const getUserVipsText = () => {
    const types = [];
    if (
      gdVipInfo?.materialVip?.length > 0 &&
      !gdVipInfo?.isMaterialVipExpired
    ) {
      types.push("素材下载会员");
    }
    if (gdVipInfo?.gdesignProductMod && !gdVipInfo?.isGdesignVipExpired) {
      types.push("模板会员");
    }
    if (gdVipInfo?.isPlus) {
      types.push("大会员");
    }
    return types.length > 0 ? types.join("、") : "未订购会员";
  };

  const handleClickSkuItem = async (skuItem: any) => {
    if (!user) {
      login();
      return;
    }

    if (!hasPermission("renewalUpgrade")) {
      Modal.warning({
        title: "提示",
        content: "无权限，请联系管理员购买会员",
      });
      return;
    }

    const trade_type = skuItem.trade_type;
    const seatType = skuItem.seat_type;

    if (trade_type === "plus") {
      // 大会员
      BuyVipDialog.open({
        buyType: "plus",
        trackerData: {
          source: "顶部导航栏",
          source_location: "会员下拉菜单",
        },
      });
    } else if (seatType === "gdesign") {
      // 模板会员
      BuyVipDialog.open({
        buyType: "Vip",
        seatType: "gdesign",
        trackerData: {
          source: "顶部导航栏",
          source_location: "会员下拉菜单",
        },
      });
    } else if (seatType === "gdmaterial") {
      // 素材会员
      BuyVipDialog.open({
        buyType: "Vip",
        seatType: "gdmaterial",
        trackerData: {
          source: "顶部导航栏",
          source_location: "会员下拉菜单",
        },
      });
    }
  };

  const content = (
    <TrackerDiv
      className={styles.dropdownContent}
      data-gd-expose="dialog_expose"
      data-module-name="订阅浮窗"
      data-subject-name="弹窗"
    >
      {/* 会员卡片区块 */}
      <div className={styles.cards}>
        {(config.show_skus || []).map((skuItem: any, idx: number) => (
          <div
            key={skuItem.name}
            className={styles.card}
            style={{
              background:
                skuItem.card_bg ||
                "linear-gradient(220deg, #cff0ff -26.34%, #fff 115.52%)",
            }}
          >
            <div className={styles.cardTitle}>
              <img
                src={skuItem.img}
                alt={skuItem.name}
                className={styles.cardIcon}
              />
              <span>{skuItem.name}</span>
            </div>
            <div className={styles.cardContent}>
              {skuItem.activity_note.map((note: any, i: number) => (
                <div key={i} className={styles.cardRightItem}>
                  {note.desc}
                </div>
              ))}
            </div>
            <button
              className={styles.cardButton}
              style={{
                background: skuItem.button_bg || "#c3e6ff",
                color: skuItem.button_color || "#1a4bc9",
                border: "none",
              }}
              onClick={() => handleClickSkuItem(skuItem)}
              data-gd-click="button_click"
              data-module-name="订阅浮窗"
              data-subject-name="按钮"
              data-button-name={getSkuItemButtonText(skuItem, pricingConfig, {
                isOrg: orgInfo?.org?.orgClass === ORGS_CLASS_MAP.ORG,
              })}
              data-user-type={getUserVipsText()}
            >
              {getSkuItemButtonText(skuItem, pricingConfig, {
                isOrg: orgInfo?.org?.orgClass === ORGS_CLASS_MAP.ORG,
              })}
            </button>
          </div>
        ))}
      </div>

      {/* 企业服务区块 */}
      <div className={styles.company}>
        <div className={styles.companyInfo}>
          <TrackerA
            href={config.company_link}
            target="_blank"
            className={styles.companyName}
            data-gd-click="button_click"
            data-module-name="订阅浮窗"
            data-subject-name="按钮"
            data-button-name={config.company_name || "企业服务"}
            data-user-type={getUserVipsText()}
          >
            {config.company_name || "企业服务"}
            <RightOutlined className={styles.arrowIcon} />
          </TrackerA>
          <div className={styles.companyDesc}>{config.company_desc}</div>
        </div>
        <TrackerA
          href={config.company_btn_link}
          target="_blank"
          className={styles.companyBtn}
          data-gd-click="button_click"
          data-module-name="订阅浮窗"
          data-subject-name="按钮"
          data-button-name="立即咨询"
          data-user-type={getUserVipsText()}
        >
          <CustomerServiceOutlined />
          立即咨询
        </TrackerA>
      </div>

      {/* 底部说明 */}
      <div className={styles.footer}>
        <TrackerA
          href={config.price_link}
          target="_blank"
          data-gd-click="button_click"
          data-module-name="订阅浮窗"
          data-subject-name="按钮"
          data-button-name={config.price_text || "不同方案价格对比"}
          data-user-type={getUserVipsText()}
        >
          {config.price_text || "不同方案价格对比"}
        </TrackerA>
        <div className={styles.bottomNoteRight}>
          {config.other_items?.map((item: any) => (
            <TrackerA
              key={item.name}
              href={item.link}
              target="_blank"
              data-gd-click="button_click"
              data-module-name="订阅浮窗"
              data-subject-name="按钮"
              data-button-name={item.name}
              data-user-type={getUserVipsText()}
            >
              {item.img && (
                <img
                  src={item.img}
                  alt={item.name}
                  className={styles.footerLinkIcon}
                />
              )}
              {item.name}
            </TrackerA>
          ))}
        </div>
      </div>
    </TrackerDiv>
  );

  return (
    <Dropdown
      overlay={content}
      placement="bottomRight"
      mouseEnterDelay={0.3}
      mouseLeaveDelay={0.3}
      getPopupContainer={(triggerNode) => triggerNode.parentElement}
    >
      <a
        href={config.page_link || "#buyVip"}
        target={config.page_link ? "_blank" : "_self"}
      >
        <button
          className={styles.triggerButton}
          style={{ padding: 0, border: "none", background: "transparent" }}
          onClick={handleVIPButtonClick}
          data-gd-expose="button_expose"
          data-gd-click="button_click"
          data-button-name="订购会员"
          data-subject-name="按钮"
          data-module-name="顶部导航栏"
        >
          {(config.img_url || config.img_url2) && (
            <img
              src={config.img_url || config.img_url2}
              alt="订购会员"
              className={styles.triggerImg}
            />
          )}
        </button>
      </a>
    </Dropdown>
  );
};

export default MembershipDropdown;
